import { useState } from 'react';
import { Sparkles, Square } from 'lucide-react';
import { useCanvasStore } from '../../store/canvas-store';
import { useAgentRunStore } from '../../agent/agent-run-store';
import { runChatTurn, cancelChatTurn } from '../../agent/chatTurn';
import { ensurePanelChat, findPanelFrame } from '../panel/panelFrames';
import { serializeTimeline } from '../../agent/serializeTimeline';
import { getPlayer } from '../../motion/registry';
import { lottieElementsOf } from '../../lottie/scan';
import type { MotionTimeline } from '../../store/types';

/** Context block the agent needs to act on a motion brief without re-asking:
 *  the live playhead, the selected node (if any), and the serialized timeline. */
export function motionContextBlock(frameId: string): string {
  const st = useCanvasStore.getState();
  const frame = st.doc.frames.find((f) => f.id === frameId);
  const tl = frame && 'motion' in frame ? (frame as { motion?: MotionTimeline }).motion : undefined;
  const lines = [
    '[motion context]',
    `frame=${frameId} playhead=${Math.round(getPlayer(frameId)?.time ?? 0)}ms`,
  ];
  const sel = st.layerSelection;
  if (sel && sel.frameId === frameId) {
    lines.push(`selected node=${sel.cfNodeId} (the user means this element unless they say otherwise)`);
  }
  lines.push(tl && tl.tracks.length ? serializeTimeline(tl) : 'no timeline yet — create one with motion_apply');
  const lotties = frame && 'html' in frame ? lottieElementsOf((frame as { html: string }).html) : [];
  if (lotties.length) {
    lines.push(
      `lottie clips: ${lotties.map((e) => e.cfNodeId).join(', ')} — edit via get_lottie/update_lottie; "make X slower/recolor X" refers to these`,
    );
  }
  lines.push('Use motion_apply for multi-keyframe work; read the timeline above before replacing tracks.');
  return lines.join('\n');
}

/** Prompt bar in the timeline dock footer: describe motion in words; the agent
 *  builds it via motion_apply in one undoable turn, context-fed with the
 *  serialized timeline, playhead, and selected node. */
export function MotionPromptBar({ frameId }: { frameId: string }) {
  const [text, setText] = useState('');
  // Reactive busy state: find the existing panel-owned chat (if any) for this
  // frame without side-effecting; creation is deferred to submit.
  const existingChat = useCanvasStore((s) => findPanelFrame(s.doc.frames, 'chat', frameId));
  const busy = useAgentRunStore((s) => (existingChat ? !!s.runs[existingChat.id] : false));

  const submit = () => {
    const t = text.trim();
    if (!t || busy) return;
    const st = useCanvasStore.getState();
    const chatId = ensurePanelChat({ frames: st.doc.frames, dispatch: st.dispatch, anchorId: frameId });
    setText('');
    void runChatTurn(chatId, `${motionContextBlock(frameId)}\n\n${t}`);
  };

  return (
    <div className="flex items-center gap-2 border-t border-[var(--color-chip-border)] px-3 py-1.5 font-[family-name:var(--font-mono)] text-xs">
      <Sparkles size={13} className="shrink-0 text-[var(--color-accent)]" />
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
        aria-label="Describe motion"
        placeholder='Describe motion… ("cards slide in from left, 80ms stagger, spring settle")'
        className="min-w-0 flex-1 bg-transparent placeholder:text-[var(--color-text-muted)] focus:outline-none"
        disabled={busy}
      />
      {busy && existingChat && (
        <button
          onClick={() => cancelChatTurn(existingChat.id)}
          aria-label="Stop"
          title="Stop"
          className="shrink-0 text-[var(--color-danger)] hover:opacity-80"
        >
          <Square size={12} />
        </button>
      )}
    </div>
  );
}
